import { css } from '@emotion/react';
import {
  faCircleCheck, faCircleExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import {
  useCallback, useEffect, useState,
} from 'react';
import {
  ButtonComponent, ButtonStyle,
} from '~/_shared/baseComponents/buttons';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { Stack } from '~/_shared/baseComponents/layout/stack.component';
import {
  TableComponent, TableDataCellComponent, TableHeaderCellComponent, TableHeaderComponent, TableRowComponent,
} from '~/_shared/baseComponents/table';
import { BlockWarningComponent } from '~/_shared/baseComponents/warning/blockWarning.component';
import { warningColorStyle } from '~/_shared/baseComponents/warning/warning.styles';
import { ModalComponent } from '~/_shared/components/modal/modal.component';
import { OverlayLoaderComponent } from '~/_shared/components/overlay/overlayLoader.component';
import type { Theme } from '~/_shared/themes/theme.model';
import { useTranslation } from '~/_shared/utils/hooks';
import {
  type ModalProps, ModalType,
} from '~/modal/modalType.enum';
import { useModal } from '~/modal/useModal.hook';
import { mapMigrationStatusServerToDomainModel } from '~/store/mapMigration/mapMigration.factory';
import { getClientMigrationStatus } from '~/store/mapMigration/mapMigration.repository';
import type {
  V4MapMigrationStatus, V4UserMigrationStatusModel,
} from '~/store/mapMigration/types/mapMigrationStatus.types';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';

const columnWidths = ['auto', 'auto', 'auto', 'auto', 'auto'];

const successColorStyle = (theme: Theme) => css({
  color: theme.textColors.success,
});

const rowStyle = (theme: Theme) => css({
  background: theme.modalColors.contentBackground,
  color: theme.textColors.primary,
  borderBottom: `1px solid ${theme.borderColors.primary}`,
});

const alignLeftStyle = css({ textAlign: 'left' });
const alignCenterStyle = css({ textAlign: 'center' });

const UserMigrationStatusCell = ({ migratedMaps, classicMaps }: Pick<V4UserMigrationStatusModel, 'migratedMaps'|'classicMaps'>) => {
  if (migratedMaps === classicMaps) {
    return (
      <TableDataCellComponent css={[alignLeftStyle, successColorStyle]}>
        <FontAwesomeIcon icon={faCircleCheck} />
        {' '}Complete
      </TableDataCellComponent>
    );
  }
  if (migratedMaps === 0) {
    return (
      <TableDataCellComponent css={[alignLeftStyle, warningColorStyle]}>
        <FontAwesomeIcon icon={faCircleExclamation} />
        {' '}Not Started
      </TableDataCellComponent>
    );
  }

  return (
    <TableDataCellComponent css={[alignLeftStyle, warningColorStyle]}>
      <FontAwesomeIcon icon={faCircleExclamation} />
      {' '}Incomplete
    </TableDataCellComponent>
  );
};

export const MapMigrationClientStatusModalContainer = ({
  isOpen,
  onClose,
}: ModalProps) => {
  const [t] = useTranslation();
  const clientId = useClientIdSelector();
  const [migrationStatusPerUser, setMigrationStatusPerUser] = useState<V4MapMigrationStatus|null>(null);
  const { openModal: openCompleteMigrationModal } = useModal(ModalType.MigrationCompleteStep);

  const incompleteUsers = migrationStatusPerUser?.list.filter((user) => user.migratedMaps !== user.classicMaps).length ?? 0;

  const fetchMigrationStatus = useCallback(async (clientId: number) => {
    const migrationStatusServerResponse = await getClientMigrationStatus(clientId);
    setMigrationStatusPerUser(mapMigrationStatusServerToDomainModel(migrationStatusServerResponse));
  }, []);

  useEffect(() => {
    if (clientId) {
      fetchMigrationStatus(clientId);
    }

    return () => setMigrationStatusPerUser(null);
  }, [clientId, fetchMigrationStatus]);

  const openNextStepModal = useCallback(() => {
    openCompleteMigrationModal({ closeParent: onClose });
  }, [onClose, openCompleteMigrationModal]);

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onClose}
      caption={t('MapMigrationReviewStatus')}
      maxWidth={800}
      confirmButton={(
        <ButtonComponent
          buttonStyle={ButtonStyle.Primary}
          onClick={openNextStepModal}
          text={t('Continue')}
        />
      )}
      leftFooterContent={(
        <ButtonComponent
          buttonStyle={ButtonStyle.Secondary}
          onClick={onClose}
          text={t('Cancel')}
        />
      )}
      additionalContent={(migrationStatusPerUser === null) && (
        <OverlayLoaderComponent />
      )}
    >
      <Stack gap={16}>
        <Stack gap={4}>
          <div>
            {t('MapMigrationStatusTable')}
          </div>
          {(migrationStatusPerUser !== null) && (
            <TableComponent columnWidths={columnWidths}>
              <TableHeaderComponent>
                <TableHeaderCellComponent>
                  {t('Team Member')}
                </TableHeaderCellComponent>
                <TableHeaderCellComponent>
                  {t('Email')}
                </TableHeaderCellComponent>
                <TableHeaderCellComponent>
                  Classic Maps
                </TableHeaderCellComponent>
                <TableHeaderCellComponent>
                  Migrated Maps
                </TableHeaderCellComponent>
                <TableHeaderCellComponent>
                  Status
                </TableHeaderCellComponent>
              </TableHeaderComponent>
              {migrationStatusPerUser.list.map(userMigrationStatus => {
                return (
                  <TableRowComponent
                    key={userMigrationStatus.id}
                    css={rowStyle}
                  >
                    <TableDataCellComponent css={alignLeftStyle}>
                      {userMigrationStatus.name}
                    </TableDataCellComponent>
                    <TableDataCellComponent css={alignLeftStyle}>
                      {userMigrationStatus.email}
                    </TableDataCellComponent>
                    <TableDataCellComponent css={alignCenterStyle}>
                      {userMigrationStatus.classicMaps}
                    </TableDataCellComponent>
                    <TableDataCellComponent css={alignCenterStyle}>
                      {userMigrationStatus.migratedMaps}
                    </TableDataCellComponent>
                    <UserMigrationStatusCell
                      migratedMaps={userMigrationStatus.migratedMaps}
                      classicMaps={userMigrationStatus.classicMaps}
                    />
                  </TableRowComponent>
                );
              })}
            </TableComponent>
          )}
        </Stack>
        {(incompleteUsers > 0) && (
          <BlockWarningComponent>
            {incompleteUsers} user{incompleteUsers > 1 ? 's' : ''} have not migrated all their maps. We recommend contacting them before completing migration.
          </BlockWarningComponent>
        )}
      </Stack>
    </ModalComponent>
  );
};
